/**
 * GameResultModal constants.
 * Copy, dimensions, and semantic colors for win/lose/draw states.
 */

import { Dimensions } from 'react-native';
import { AVATAR_IDS, type AvatarId } from '../../../constants/avatarIds';

const { width: SCREEN_W } = Dimensions.get('window');

export const GAME_RESULT_DIMENSIONS = {
  CARD_W: Math.min(SCREEN_W - 32, 320),
  SCREEN_W,
} as const;

export const DEFAULT_AVATARS = {
  HOME: AVATAR_IDS.BLUE_WITH_HAIR as AvatarId,
  AWAY: AVATAR_IDS.MOYKAN_BALL as AvatarId,
} as const;

/** Per-outcome card frame colors — gold / silver / violet */
export const CARD_FRAME_COLORS = {
  won: {
    accent: '#FFD700',
    frame: '#FCD34D',
    innerBorderBg: '#92400E',
    frameGradient: ['#FCD34D', '#92400E', '#FCD34D'] as readonly [string, string, string],
    plateGradient: ['#FBBF24', '#B45309'] as readonly [string, string],
    plateText: '#3B1A02',
    stageGlow: 'rgba(252,211,77,0.22)',
    coinSign: '+',
    coinColor: '#FCD34D',
    subtitle: 'You called it.',
    cardTitle: 'VICTORY',
    holoColors: ['transparent', 'rgba(255,255,255,0.20)', 'rgba(167,139,250,0.18)', 'rgba(110,231,183,0.18)', 'transparent'] as readonly [string, string, string, string, string],
  },
  lost: {
    accent: '#F87171',
    frame: '#9CA3AF',
    innerBorderBg: '#374151',
    frameGradient: ['#9CA3AF', '#374151', '#9CA3AF'] as readonly [string, string, string],
    plateGradient: ['#6B7280', '#1F2937'] as readonly [string, string],
    plateText: '#F7FAFC',
    stageGlow: 'rgba(156,163,175,0.15)',
    coinSign: '−',
    coinColor: '#F87171',
    subtitle: "Next one's yours.",
    cardTitle: 'DEFEAT',
    holoColors: ['transparent', 'rgba(255,255,255,0.07)', 'rgba(255,255,255,0.05)', 'transparent'] as readonly [string, string, string, string],
  },
  draw: {
    accent: '#A78BFA',
    frame: '#A78BFA',
    innerBorderBg: '#4C1D95',
    frameGradient: ['#A78BFA', '#4C1D95', '#A78BFA'] as readonly [string, string, string],
    plateGradient: ['#A78BFA', '#4C1D95'] as readonly [string, string],
    plateText: '#F7FAFC',
    stageGlow: 'rgba(167,139,250,0.20)',
    coinSign: '±',
    coinColor: '#FCD34D',
    subtitle: 'Split the points.',
    cardTitle: 'STALEMATE',
    holoColors: ['transparent', 'rgba(252,211,77,0.14)', 'rgba(167,139,250,0.14)', 'transparent'] as readonly [string, string, string, string],
  },
} as const;

export type CardFrameKey = keyof typeof CARD_FRAME_COLORS;
export type CardFrame = (typeof CARD_FRAME_COLORS)[CardFrameKey];

export const RESULT_COPY = {
  LABEL_YOU: 'YOU',
  LABEL_RIVAL: 'RIVAL',
  CTA_WON: 'Tap to Claim',
  CTA_LOST: 'Tap to Continue',
  CTA_DRAW: 'Tap to Continue',
  SCORE_LABEL: 'Points',
  HITS_LABEL: 'Correct',
  OVERFLOW_HINT_TEXT: '+ more. Check the Finished tab to see all.',
} as const;
